// Icons
import { ReactComponent as CustomerEngTrailIcon } from "@/assets/icons/header/dataPages/CustomerEngTrail.svg";
import { ReactComponent as CallActivityReportIcon } from "@/assets/icons/header/dataPages/CallActivityReportIcon.svg";
import { ReactComponent as ContractsInTransitIcon } from "@/assets/icons/header/dataPages/ContractsInTransitIcon.svg";
import { ReactComponent as TimePayRatesIcon } from "@/assets/icons/header/dataPages/TimePayRates.svg";
import { ReactComponent as UsersIcon } from "@/assets/icons/header/dataPages/UsersIcon.svg";
import { ReactComponent as ChargebacksIcon } from "@/assets/icons/header/dataPages/ChargebacksIcon.svg";
import { ReactComponent as SalesOperationIcon } from "@/assets/icons/header/dataPages/SalesOperationsIcon.svg";
import { ReactComponent as OemIcon } from "@/assets/icons/header/dataPages/OemDataIcon.svg";
import { ReactComponent as DrawIcon } from "@/assets/icons/header/dataPages/DrawIcon.svg";
import { ReactComponent as InventoryIcon } from "@/assets/icons/header/dataPages/Inventory.svg";
import { ReactComponent as CRMIcon } from "@/assets/icons/header/dataPages/CRMIcon.svg";
import { ReactComponent as ServiceOperationsIcon } from "@/assets/icons/header/dataPages/ServiceOperationsIcon.svg";

// Components
import RenderMenusOptions, { TRenderOptionConfiguration } from "./RenderMenusOptions";
// Constants
import actions from "@/Constants/actionsList";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useWrapperLayer } from "@/Common/context/wrapperLayer/WrapperLayerContext";

export const DATA_CENTER_MENU_REQUIRED_ACTIONS = [
	// Sales Operations
	actions.VIEW_SALES_PERFORMANCE_REPORTS,
	actions.VIEW_SALES_LOG,
	// Service Operations
	actions.VIEW_TECHNICIAN_FLAGGED_TIME,
	actions.VIEW_ADVISOR_PERFORMANCE,
	actions.VIEW_REPAIR_ORDERS,
	// Customer Relations
	actions.VIEW_SOCIAL_MEDIA_REVIEWS,
	actions.VIEW_CUSTOMER_SATISFACTION_SCORES,
	// Draw and Overdraws
	actions.VIEW_DRAW_AMOUNTS,
	// User
	actions.VIEW_USERS,
	// Chargebacks
	actions.VIEW_CHARGEBACKS_REPORTS,
	// Pay Rates
	actions.VIEW_PAY_RATES,
	// Time and attendance
	actions.VIEW_TIME_AND_ATTENDANCE,
	// Customer Engagement Trail
	actions.VIEW_CUSTOMER_ENGAGEMENT_TRAIL,
	// F&I Management
	actions.VIEW_ALL_DEALS,
	actions.VIEW_CIT_REPORTS,
	actions.VIEW_FINANCE_DEAL_DATA,
	actions.VIEW_FINANCE_DETAILS,
	actions.VIEW_FINANCE_TRADES_PAYOFFS,
	actions.VIEW_FINANCE_TITLINGS,
	actions.VIEW_CANCELLATIONS,
	// Inventory
	actions.VIEW_INVENTORY,
	actions.VIEW_DEALER_TRADES,
	actions.VIEW_RECONCILIATION,
];

export default function DataCenterMenu() {
	const { useMultipleAuth } = useAuthorization();
	const { isDisabled } = useWrapperLayer();

	const isAuthorized = useMultipleAuth(DATA_CENTER_MENU_REQUIRED_ACTIONS, "atLeastOne");

	const options: TRenderOptionConfiguration[] = [
		{
			title: "Sales Operations",
			link: "/sales-operations",
			icon: <SalesOperationIcon />,
			requiredActions: [actions.VIEW_SALES_PERFORMANCE_REPORTS, actions.VIEW_SALES_LOG],
		},
		{
			title: "Service Operations",
			link: "/service-operations",
			icon: <ServiceOperationsIcon />,
			requiredActions: [actions.VIEW_REPAIR_ORDERS, actions.VIEW_ADVISOR_PERFORMANCE, actions.VIEW_TECHNICIAN_FLAGGED_TIME],
		},
		{
			title: "Chargebacks",
			link: "/chargebacks",
			icon: <ChargebacksIcon />,
			requiredActions: [actions.VIEW_CHARGEBACKS_REPORTS],
		},
		{
			title: "OEM Data",
			link: "/oem-data",
			icon: <OemIcon />,
			requiredActions: [actions.VIEW_CUSTOMER_SATISFACTION_SCORES],
		},
		{
			title: "Users",
			link: "/users",
			icon: <UsersIcon />,
			requiredActions: [actions.VIEW_USERS],
		},
		{
			title: "Draw and Overdraw",
			link: "/draw",
			icon: <DrawIcon />,
			requiredActions: [actions.VIEW_DRAW_AMOUNTS],
		},
		{
			title: "Time and Pay Rates",
			link: "/time-and-pay-rates",
			icon: <TimePayRatesIcon />,
			requiredActions: [actions.VIEW_TIME_PAY_RATES_REPORTS],
		},
		{
			title: "F&I Management",
			link: "/fni-management",
			icon: <ContractsInTransitIcon />,
			requiredActions: [
				actions.VIEW_CIT_REPORTS,
				actions.VIEW_FINANCE_DEAL_DATA,
				actions.VIEW_FINANCE_DETAILS,
				actions.VIEW_FINANCE_TRADES_PAYOFFS,
				actions.VIEW_FINANCE_TITLINGS,
				actions.VIEW_CANCELLATIONS,
			],
		},
		{
			title: "Call Activity Report",
			link: "/call-activity-report",
			icon: <CallActivityReportIcon />,
			requiredActions: [actions.VIEW_CALL_ACTIVITY_REPORTS],
		},
		{
			title: "Customer Engagement Trail",
			link: "/customer-engagement-trail",
			icon: <CustomerEngTrailIcon />,
			requiredActions: [actions.VIEW_CUSTOMER_ENGAGEMENT_TRAIL],
		},
		{
			title: "Dealer Inventory",
			link: "/inventory",
			icon: <InventoryIcon />,
			requiredActions: [actions.VIEW_INVENTORY],
		},
		{
			title: "Customer Relations",
			link: "/customer-relation-management",
			icon: <CRMIcon />,
			requiredActions: [
				actions.VIEW_SALES_LEADS,
				actions.VIEW_DISPOSITIONS,
				actions.VIEW_SALES_LEAD_CUSTOMERS,
				actions.VIEW_SOCIAL_MEDIA_REVIEWS,
			],
		},
	];

	if (!isAuthorized || isDisabled) {
		return null;
	}

	return <RenderMenusOptions arrayOptions={options} />;
}
